import { toNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { DNSlideTabItemType } from '@/designComponents/organisms/DNSlideTab/DNSlideTabItem';
import useSetAuthOnScreenFocus from '@/hooks/auth/useSetAuthOnScreenFocus';
import useGetUserAddressByLocation from '@/hooks/hospital/search/useGetUserAddressByLocation';
import useGetKeywordIntegratedSearchHospital from '@/hooks/search/useGetKeywordIntegratedSearchHospital';
import useGetPopularKeywordsForDiseaseSymptomSearch from '@/hooks/search/useGetPopularKeywordsForDiseaseSymptomSearch';
import useInfiniteSearchNonF2fDoctors from '@/hooks/search/useInfiniteSearchNonF2fDoctors';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal, { AddModalProps } from '@/hooks/useModal';
import useToast from '@/hooks/useToast';
import { recentHomeSearchKeywordListState } from '@/recoil/atoms/search';
import { SearchHospitalService } from '@/services/hospital/SearchHospitalService';
import { HomeSearchService } from '@/services/search/HomeSearchService';
import { Client } from '@/types/app';
import {
  DoctorSortType,
  DoctorType,
  MedicalCategoryType,
  TelemedicineEligibleTargetType,
} from '@/types/bff/gql-schema';
import { APP_SCREEN_NAME, ROUTES } from '@/types/common';
import { RegionByDepthVo, SearchHospitalVo, UserCoordinate } from '@/types/hospital';
import { Magazine } from '@/types/magazine';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import {
  DoctorSearchResultVo,
  EGeneralSearchSnippetType,
  EIntegratedSearchTabType,
  GeneralSearchMetaByKeywordVo,
  HomeSearchKeywordList,
  MagazineSearchResultVo,
  NonF2fDoctorVo,
  QuestionSearchResultVo,
  SearchResult,
} from '@/types/search';
import { coreLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, historyBack, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { updateSearchParams } from '@/utils/url';

import { SearchLoggerEventName } from '@/constants/logs/search';
import MedicineDictionary from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import useAdBanner from '@/hooks/ad/useAdBanner';
import { AdPageType, BannerAdExchangeModel } from '@/models/ad/list';
import { EDeprecatedCategoryType } from '@/services/doctor/DiagnosisCategoryService';
import Airbridge from '@/utils/airbridge';
import { validateNonSpecialPattern } from '@/utils/validate';
import useLogger from '../useLogger';
import useNavigate from '../useNavigate';
import useWebViewBackEventHandler from '../useWebViewBackEventHandler';
import useGeneralSearchMetaByKeyword from './useGeneralSearchMetaByKeyword';
import useGetKeywordIntegratedSearchMagazine from './useGetKeywordIntegratedSearchMagazine';
import useGetKeywordIntegratedSearchMedicineDictionary from './useGetKeywordIntegratedSearchMedicineDictionary';
import useGetKeywordIntegratedSearchQna from './useGetKeywordIntegratedSearchQna';
interface Modal {
  isOpen: boolean;
  info: AddModalProps;
}

const INITIAL_MODAL_STATE = {
  isOpen: false,
  info: {
    type: undefined,
    props: {
      title: '',
      buttons: [],
    },
  },
};

const INTEGRATED_SEARCH_RESULT_TABS = [
  {
    key: 0,
    label: '통합',
    value: EIntegratedSearchTabType.All,
  },
  {
    key: 1,
    label: '비대면 진료',
    value: EIntegratedSearchTabType.Doctor,
  },
  {
    key: 2,
    label: '가까운 병원 찾기',
    value: EIntegratedSearchTabType.Hospital,
  },
  {
    key: 3,
    label: '실시간 의료상담',
    value: EIntegratedSearchTabType.Qna,
  },
  {
    key: 4,
    label: '건강 매거진',
    value: EIntegratedSearchTabType.Magazine,
  },
  {
    key: 5,
    label: '의약품 사전',
    value: EIntegratedSearchTabType.MedicineDictionary,
  },
];

export interface HomeSearchViewModel {
  searchInputRef: React.RefObject<HTMLInputElement>;
  keyword: string;
  isSnackBarOpen: boolean;
  recentKeywordViewData: HomeSearchKeywordList;
  hasNoData: boolean;
  modal: Modal;
  popularSymptomList: Array<{
    symptomName: string;
    symptomPublicId: string;
  }>;
  isSearchResultShow: boolean;
  INTEGRATED_SEARCH_RESULT_TABS: Array<DNSlideTabItemType<EIntegratedSearchTabType>>;
  selectedTabValue: EIntegratedSearchTabType;
  keywordSearchQna?: SearchResult<QuestionSearchResultVo>;
  keywordSearchMagazine?: SearchResult<MagazineSearchResultVo>;
  keywordSearchMedicineDictionary?: SearchResult<MedicineDictionary>;
  keywordSearchDoctor?: SearchResult<DoctorSearchResultVo>;
  keywordSearchHospital?: SearchResult<SearchHospitalVo>;
  coordinate: UserCoordinate;
  geolocationPermissionLoading: boolean;
  doctors: Array<NonF2fDoctorVo>;
  hasMoreDoctors: boolean;
  category: { category: MedicalCategoryType; id: string } | null;
  generalSearchMetaByKeyword?: GeneralSearchMetaByKeywordVo;
  isIntegratedSearchLoading: boolean;
  bannerAdExchange: BannerAdExchangeModel | null;
  handleSendExposureLog: () => void;
  handleInputChange: (value: string) => void;
  handleSnackBarClose: () => void;
  handleSubmit: () => void;
  handleResetSearchKeyword: (e?: React.MouseEvent) => void;
  handleSearchDirectKeyword: (keyword: string) => void;
  handleDeleteItemRecentKeyword: (keyword: string) => void;
  handleDeleteAllRecentKeyword: () => void;
  handleChangeKeyword: () => void;
  handleClickRequestButton: (buttonPosition: string) => void;
  handleClickQuestion: (questionId: number | string) => void;
  handleClickMagazine: (magazine: Magazine) => Promise<void>;
  handleClickDoctor: (doctor: DoctorSearchResultVo) => Promise<void>;
  handleClickMedicineDictionary: (medicineDictionary: MedicineDictionary) => () => void;
  handleClickMoreData: (type: EIntegratedSearchTabType) => () => void;
  handleSymptomKeywordClick: (keyword: string) => void;
  onClickTabItem: (tab: DNSlideTabItemType<EIntegratedSearchTabType>) => void;
  onChangeTabByMoreData: (type: EIntegratedSearchTabType) => void;
  goToAppHospital: (hospitalPid: string) => void;
  getHeaderAddress: () => string;
  requestGeolocationPermission: () => void;
  navigateToDoctorList: (type: EGeneralSearchSnippetType) => void;
  navigateToFindPharmacyScreen: () => void;
  navigateDietInjectionScreen: () => void;
  navigateToEmergencyContraceptiveScreen: () => void;
  handleBackButtonClick: () => void;
}

type Props = {
  client: Client;
  entryType: string;
};

const DOCTOR_LIST_LIMIT = 4;

const useHomeSearchViewModel = (props: Props): HomeSearchViewModel => {
  useSetAuthOnScreenFocus();
  const navigate = useNavigate();
  const { router, moveToPage, pageName } = useCustomRouter();
  const { closeModal: closeCurrentModal } = useModal();
  const { showToast } = useToast();
  const logger = useLogger();
  const { entryType } = props;
  const { latitude: queryLatitude, longitude: queryLongitude } = router.query as {
    latitude: string;
    longitude: string;
  };
  const shouldSnackBarOpen = router.query.shouldSnackBarOpen as string;
  const { keyword = '' } = router.query as { keyword: string };

  const [regionByDepth, setRegionByDepth] = useState<RegionByDepthVo>();
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const [isSearchResultShow, setIsSearchResultShow] = useState<boolean>(!!keyword);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [recentKeywordList, setRecentKeywordList] = useRecoilState(recentHomeSearchKeywordListState);
  const [modal, setModal] = useState<Modal>({ ...INITIAL_MODAL_STATE });
  const [geolocationPermissionLoading, setGeolocationPermissionLoading] = useState(false);
  const [recentKeywordViewData, setRecentKeywordViewData] = useState<HomeSearchKeywordList>([]);
  const [selectedTabValue, setSelectedTabValue] = useState<EIntegratedSearchTabType>(
    (router.query.tab as EIntegratedSearchTabType) ?? EIntegratedSearchTabType.All
  );

  const initialCoord = useMemo(() => {
    if (!queryLatitude || !queryLongitude) {
      return SearchHospitalService.initializeUserCoordinate();
    }

    const latitude = toNumber(queryLatitude);
    const longitude = toNumber(queryLongitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      return SearchHospitalService.initializeUserCoordinate();
    }

    return { latitude, longitude };
  }, [queryLatitude, queryLongitude]);
  const [coordinate, setCoordinate] = useState<UserCoordinate>(initialCoord);

  const { data: sigunguDongli = SearchHospitalService.initializeUserSigunguDongli(), refetch: addressRefetch } =
    useGetUserAddressByLocation(coordinate.latitude, coordinate.longitude);

  const { bannerAdExchange, handleSendExposureLog } = useAdBanner(AdPageType.Search, props.client);

  const { keywordSearchQna, isKeywordSearchQnaLoading } = useGetKeywordIntegratedSearchQna(keyword);
  const { keywordSearchMagazine, isKeywordSearchMagazineLoading } = useGetKeywordIntegratedSearchMagazine(keyword);
  const { keywordSearchHospital, isKeywordSearchHospitalLoading } = useGetKeywordIntegratedSearchHospital(
    keyword,
    coordinate.latitude,
    coordinate.longitude
  );
  const { keywordSearchMedicineDictionary, isKeywordSearchMedicineDictionaryLoading } =
    useGetKeywordIntegratedSearchMedicineDictionary({
      keyword,
      enabled: !!keyword && keyword.length > 0,
    });

  const {
    items,
    hasNextPage,
    category,
    isLoading: isDoctorLoading,
  } = useInfiniteSearchNonF2fDoctors(
    {
      request: {
        keyword: keyword,
        sortType: DoctorSortType.Random,
        page: 1,
        limit: DOCTOR_LIST_LIMIT,
        doctorType: DoctorType.Doctor,
        telemedicineEligibleTargetType: TelemedicineEligibleTargetType.NightAndHolidayOnly,
      },
    },
    { enabled: !!keyword }
  );
  const { generalSearchMetaByKeyword } = useGeneralSearchMetaByKeyword({ keyword });

  useWebViewBackEventHandler({
    isBackEventTrigger: isSearchResultShow,
    callback: () => {
      handleResetSearchKeyword();
    },
  });

  const trimmedDoctors = [...items].splice(0, DOCTOR_LIST_LIMIT - 1);

  const getHeaderAddress = () => {
    let address = `${sigunguDongli.sigungu} ${sigunguDongli.dongLi}`;

    if (regionByDepth?.siDoName) {
      if (!regionByDepth.siGunGuName && !regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName}`;
      } else if (regionByDepth.siGunGuName && !regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName} ${regionByDepth.siGunGuName}`;
      } else if (regionByDepth.siGunGuName && regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siGunGuName} ${regionByDepth.legalEupMyeonDongName}`;
      } else if (!regionByDepth.siGunGuName && regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName} ${regionByDepth.legalEupMyeonDongName}`;
      }
    }
    return address;
  };

  const initRegionFilter = () => {
    setRegionByDepth(undefined);
  };

  const requestGeolocationPermission = useCallback(() => {
    if (!navigator.geolocation) {
      showToast({
        message: '다른 브라우저로 해당 기능을 이용해주세요',
      });
      setCoordinate({
        latitude: initialCoord.latitude,
        longitude: initialCoord.longitude,
      });
      return;
    }
    setGeolocationPermissionLoading(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        initRegionFilter();
        setGeolocationPermissionLoading(false);
        updateSearchParams([
          {
            key: 'latitude',
            value: coords.latitude,
          },
          {
            key: 'longitude',
            value: coords.longitude,
          },
        ]);
      },
      () => {
        setGeolocationPermissionLoading(false);
        showToast({
          message: '위치 사용을 허용을 해야 내 위치 설정이 가능해요',
        });
      },
      { enableHighAccuracy: true }
    );
  }, [initialCoord, showToast]);

  const setSearchKeyword = async (newKeyword: string) => {
    setIsSearchResultShow(true);
    await updateSearchParams([{ key: 'keyword', value: newKeyword }]);
  };

  const handleInputChange = (value: string) => {
    updateSearchParams([{ key: 'keyword', value: value }]);
  };

  const closeModal = () => {
    closeCurrentModal();
    setModal(INITIAL_MODAL_STATE);
  };

  const handleSubmit = () => {
    const newKeywordList = HomeSearchService.getNewKeywordList([...recentKeywordList], keyword);

    setRecentKeywordList(newKeywordList);
    searchInputRef.current?.blur();
  };

  // 검색 버튼 클릭
  const handleChangeKeyword = async () => {
    const newKeyword = searchInputRef.current?.value ?? '';
    if (!newKeyword.trim()) {
      setModal({
        isOpen: true,
        info: {
          type: MODAL_TYPE.ALERT,
          props: {
            title: '검색어를 입력해주세요.',
            buttons: [
              {
                type: MODAL_BUTTON_TYPE.BOLD,
                label: '확인',
                onClick: () => closeModal(),
              },
            ],
          },
        },
      });

      return;
    }

    if (newKeyword.length < 2) {
      setModal({
        isOpen: true,
        info: {
          type: MODAL_TYPE.ALERT,
          props: {
            title: '2글자 이상 입력해 주세요.',
            message: '더 정확도 높은 결과를 볼 수 있어요.',
            buttons: [
              {
                type: MODAL_BUTTON_TYPE.BOLD,
                label: '확인',
                onClick: () => closeModal(),
              },
            ],
          },
        },
      });

      return;
    }

    if (!validateNonSpecialPattern(newKeyword)) {
      setModal({
        isOpen: true,
        info: {
          type: MODAL_TYPE.ALERT,
          props: {
            title: '특수문자를 제외하고 입력해주세요.',
            message: '더 정확도 높은 결과를 볼 수 있어요.',
            buttons: [
              {
                type: MODAL_BUTTON_TYPE.BOLD,
                label: '확인',
                onClick: () => closeModal(),
              },
            ],
          },
        },
      });
      return;
    }

    coreLogger({
      eventName: 'click_search',
      pageName,
      payload: {
        section: 'search_bar',
        keyword: newKeyword,
        entry_point: entryType,
      },
    });

    await setSearchKeyword(newKeyword);
  };

  const handleResetSearchKeyword = (e?: React.MouseEvent) => {
    e?.preventDefault();
    searchInputRef.current?.blur();
    setIsSearchResultShow(false);
    updateSearchParams([
      { key: 'keyword', value: '' },
      { key: 'tab', value: '' },
    ]);
    setSelectedTabValue(EIntegratedSearchTabType.All);
    if (keyword && searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  // 최근 검색어 / 자주 찾는 검색어로 검색
  const handleSearchDirectKeyword = async (keyword: string) => {
    coreLogger({
      eventName: 'click_search',
      pageName,
      payload: {
        section: 'recent',
        keyword,
        entry_point: entryType,
      },
    });
    await setSearchKeyword(keyword);
  };

  // 최근 검색어 선택 삭제
  const handleDeleteItemRecentKeyword = (keyword: string): void => {
    const filter = recentKeywordViewData.filter((recentKeyword) => keyword !== recentKeyword);

    setRecentKeywordList(filter);
  };

  // 최근 검색어 전체 삭제
  const handleDeleteAllRecentKeyword = (): void => {
    setRecentKeywordList([]);
  };

  const handleSnackBarClose = (): void => {
    setIsSnackBarOpen(false);
  };

  const handleClickRequestButton = (buttonPosition: string): void => {
    coreLogger({
      eventName: 'request_additional_info_click',
      pageName,
      payload: {
        position: buttonPosition,
        keyword,
      },
    });
    moveToPage({
      webLink: `${ROUTES.SEARCH(keyword.replaceAll(' ', '+'), entryType).REQUEST_INFO.path}`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+'), entryType).REQUEST_INFO.appScreenName,
      params: { keyword },
    });
  };

  const handleClickQuestion = (questionId: number | string) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: questionId,
        tab: EIntegratedSearchTabType.All,
        result_section: EIntegratedSearchTabType.Qna,
      },
    });

    navigate.navigateToQnaDetail({
      questionPid: String(questionId),
      result_section: EIntegratedSearchTabType.Qna,
    });
  };

  const handleClickDoctor = async (doctor: DoctorSearchResultVo) => {
    if (!isWebView()) {
      return;
    }

    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: doctor.doctorId,
        tab: EIntegratedSearchTabType.All,
        result_section: EIntegratedSearchTabType.Doctor,
      },
    });

    sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
      screenName: APP_SCREEN_NAME.DOCTOR_DETAIL,
      params: {
        doctorId: doctor.doctorId,
        type: 'subject',
        entry: 'search',
      },
    });
  };

  const handleClickMagazine = async (magazine: Magazine) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: magazine.magazinePid,
        tab: EIntegratedSearchTabType.All,
        result_section: EIntegratedSearchTabType.Magazine,
      },
    });
    moveToPage({
      webLink: `${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`,
      mobileScreenName: ROUTES.MAGAZINE.DETAIL.appScreenName,
      params: {
        id: magazine.magazinePid,
      },
    });
  };

  const handleClickMedicineDictionary = (medicineDictionary: MedicineDictionary) => () => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: medicineDictionary.getPublicId(),
        tab: EIntegratedSearchTabType.All,
        result_section: EIntegratedSearchTabType.MedicineDictionary,
      },
    });
    navigate.navigateToMedicineDictionaryDetail({
      medicineDictionaryPid: medicineDictionary.getPublicId(),
      keyword,
    });
  };

  const goToAppHospital = (hospitalPid: string) => {
    if (isWebView()) {
      coreLogger({
        eventName: 'search_item_click',
        pageName,
        payload: {
          keyword,
          content_id: hospitalPid,
          tab: EIntegratedSearchTabType.All,
          result_section: EIntegratedSearchTabType.Hospital,
        },
      });
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
        screenName: ROUTES.HOSPITAL.DETAIL.appScreenName,
        params: { hospitalPid: hospitalPid },
      });
    }
  };

  const handleClickMoreData = useCallback(
    (type: EIntegratedSearchTabType) => () => {
      coreLogger({
        eventName: 'search_item_more_click',
        pageName,
        payload: {
          keyword,
          result_section: type,
        },
      });
      moveToPage({
        webLink: `${ROUTES.COMMON.SEARCH.path}?${
          entryType ? `entryType=${entryType}` : ''
        }&keyword=${keyword.replaceAll(' ', '+')}`,
        mobileScreenName: ROUTES.COMMON.SEARCH.appScreenName,
        params: {
          keyword,
        },
      });
    },
    [keyword]
  );

  const onChangeTabByMoreData = (type: EIntegratedSearchTabType) => {
    coreLogger({
      eventName: 'search_item_more_click',
      pageName,
      payload: {
        keyword,
        result_section: type,
      },
    });
    if (isWebView() && type === EIntegratedSearchTabType.Doctor && category) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_DOCTOR_LIST_WITH_TARGET_CHECK, {
        type: category.category,
        id: category.id,
      });
      return;
    }
    setSelectedTabValue(type);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const { popularSymptomList } = useGetPopularKeywordsForDiseaseSymptomSearch();

  const handleSymptomKeywordClick = async (keyword: string) => {
    setIsSearchResultShow(true);
    await setSearchKeyword(keyword);
    coreLogger({
      eventName: 'click_search',
      pageName,
      payload: {
        keyword,
        section: 'popular_symptom',
        entry_point: entryType,
      },
    });
  };

  // 탭 클릭 handler
  const onClickTabItem = async (tab: DNSlideTabItemType<EIntegratedSearchTabType>) => {
    coreLogger({
      eventName: 'integrated_search_tab_click',
      pageName,
      payload: {
        tab: tab.value,
      },
    });

    setSelectedTabValue(tab.value);
    await updateSearchParams([{ key: 'tab', value: tab.value }]);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const navigateToDoctorList = (type: EGeneralSearchSnippetType) => {
    const eventName =
      type === EGeneralSearchSnippetType.NonFaceToFaceDiagnosis
        ? SearchLoggerEventName.NonFaceToFaceDiagnosisClickInSnippet
        : SearchLoggerEventName.NonFaceToFacePrescriptionClickInSnippet;
    logger.log({
      eventName,
      payload: {
        keyword,
      },
    });
    onChangeTabByMoreData(EIntegratedSearchTabType.Doctor);
  };
  const navigateToFindPharmacyScreen = () => {
    logger.log({
      eventName: SearchLoggerEventName.FindPharmacyClickInSnippetFeature,
      payload: {
        keyword,
      },
    });
    navigate.navigateToFindPharmacy();
  };

  const navigateToEmergencyContraceptiveScreen = () => {
    if (!isWebView()) {
      Airbridge.setDynamicLink({
        campaign: 'web_home_search',
        content: 'web_emergency_contraceptive_snippet',
        term: '사후피임약 진료 예약',
        sub_id: keyword,
      });
      return;
    }
    logger.log({
      eventName: SearchLoggerEventName.EmergencyContraceptiveClickInSnippetFeature,
      payload: {
        keyword,
      },
    });

    const CONTRACEPTIVE_SYMPTOM_CATEGORY_ID = 10;
    const type = EDeprecatedCategoryType.Symptom;

    navigate.navigateToEmergencyContraceptive({
      categoryId: CONTRACEPTIVE_SYMPTOM_CATEGORY_ID,
      type,
    });
  };

  const navigateDietInjectionScreen = () => {
    if (!isWebView()) {
      Airbridge.setDynamicLink({
        campaign: 'web_home_search',
        content: 'web_diet_injection_snippet',
        term: '다이어트 주사 받기',
        sub_id: keyword,
      });
      return;
    }
    logger.log({
      eventName: SearchLoggerEventName.DietInjectionClickInSnippetFeature,
      payload: {
        keyword,
      },
    });
    navigate.navigateToDietInjection();
  };

  const handleBackButtonClick = () => {
    if (isSearchResultShow) {
      handleResetSearchKeyword();
      return;
    }
    historyBack();
  };

  const isIntegratedSearchLoading = useMemo(() => {
    return (
      isKeywordSearchQnaLoading ||
      isKeywordSearchMagazineLoading ||
      isKeywordSearchHospitalLoading ||
      isDoctorLoading ||
      isKeywordSearchMedicineDictionaryLoading
    );
  }, [
    isKeywordSearchQnaLoading,
    isKeywordSearchMagazineLoading,
    isKeywordSearchHospitalLoading,
    isDoctorLoading,
    isKeywordSearchMedicineDictionaryLoading,
  ]);

  useEffect(() => {
    if (!shouldSnackBarOpen) {
      return;
    }

    setIsSnackBarOpen(true);
    router.replace('/search');
  }, []);

  useEffect(() => {
    if (keyword && searchInputRef.current) {
      searchInputRef.current.value = keyword;
      setIsSearchResultShow(true);
    }
  }, [keyword]);

  useEffect(() => {
    if (!router.query.keyword) {
      setIsSearchResultShow(false);
    }
  }, [router.asPath, router.query.keyword]);

  useEffect(() => {
    setRecentKeywordViewData(recentKeywordList);
  }, [recentKeywordList]);

  useEffect(() => {
    const focusInput = () => searchInputRef?.current?.focus();
    focusInput();
  }, []);

  useEffect(() => {
    if (queryLatitude && queryLongitude) {
      setCoordinate({
        latitude: toNumber(queryLatitude),
        longitude: toNumber(queryLongitude),
      });
    }
  }, [queryLatitude, queryLongitude]);

  useEffect(() => {
    addressRefetch();
  }, [coordinate]);

  return {
    handleSendExposureLog,
    bannerAdExchange,
    searchInputRef,
    keyword,
    isSnackBarOpen,
    recentKeywordViewData: recentKeywordViewData.filter((item) => typeof item === 'string'),
    modal,
    popularSymptomList,
    isSearchResultShow,
    INTEGRATED_SEARCH_RESULT_TABS,
    selectedTabValue,
    keywordSearchQna,
    keywordSearchMagazine,
    keywordSearchHospital,
    keywordSearchMedicineDictionary,
    coordinate,
    geolocationPermissionLoading,
    hasNoData:
      !keywordSearchQna?.elements.length &&
      !keywordSearchMagazine?.elements.length &&
      !keywordSearchHospital?.elements.length &&
      !keywordSearchMedicineDictionary?.elements.length &&
      !trimmedDoctors?.length,
    doctors: trimmedDoctors,
    hasMoreDoctors: !!hasNextPage,
    category,
    generalSearchMetaByKeyword,
    isIntegratedSearchLoading,
    handleInputChange,
    handleSnackBarClose,
    handleSubmit,
    handleResetSearchKeyword,
    handleSearchDirectKeyword,
    handleDeleteItemRecentKeyword,
    handleDeleteAllRecentKeyword,
    handleChangeKeyword,
    handleClickRequestButton,
    handleClickQuestion,
    handleClickDoctor,
    handleClickMedicineDictionary,
    handleClickMagazine,
    handleClickMoreData,
    handleSymptomKeywordClick,
    onClickTabItem,
    onChangeTabByMoreData,
    goToAppHospital,
    getHeaderAddress,
    requestGeolocationPermission,
    navigateToDoctorList,
    navigateToFindPharmacyScreen,
    navigateDietInjectionScreen,
    handleBackButtonClick,
    navigateToEmergencyContraceptiveScreen,
  };
};

export default useHomeSearchViewModel;
